import React, { FunctionComponent } from 'react';

import { Text } from 'components/chroma';

import { ApiData } from 'scenes/common/types';
import { getApiData } from 'scenes/common/helpers/apiData';
import { toAussieStandardDateFormat } from 'scenes/common/helpers/datetime';

import {
  MotorDriverSubSection,
  MotorFullPageProps,
  MotorFunnelState,
  MotorQuoteSubSection,
  MotorSection,
  ReferenceData,
} from 'scenes/motor/types';
import {
  getMainDriver,
  getRecentInsurer,
  getCoverLevel,
} from 'scenes/motor/selectors';
import SummaryCard from 'components/SummaryCard';

const getRecentInsurerName = (
  funnelState: MotorFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const insurerCode = getRecentInsurer(funnelState);
  return (
    Object.values(getApiData(referenceData).recentInsurerOptions)
      .flat()
      .find((insurer) => insurer.value === insurerCode)?.label ?? insurerCode
  );
};

const getCoverLevelDescription = (
  funnelState: MotorFunnelState,
  referenceData: ApiData<ReferenceData>
) =>
  getApiData(referenceData).coverLevelList.find(
    (level) =>
      level.insurerCode === getRecentInsurer(funnelState) &&
      level.value === getCoverLevel(funnelState)
  )?.label || '';

export const MainDriverSummary: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditDriver' | 'referenceData'
>> = ({ funnelState, onEditQuoteDetails, onEditDriver, referenceData }) => {
  const mainDriver = getMainDriver(funnelState);
  return (
    <>
      <SummaryCard
        heading="Name"
        onEditClick={() =>
          onEditDriver({
            motorSubSection: MotorDriverSubSection.FirstAndLastName,
            motorSection: MotorSection.MainDriver,
            nameEdit: true,
            index: -1,
          })
        }
      >
        <Text>{`${mainDriver.firstName} ${mainDriver.lastName}`}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Gender & Date of birth"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.MainDriver,
            MotorQuoteSubSection.GenderAndDateOfBirth
          )
        }
      >
        <Text>{`${mainDriver.gender} - ${toAussieStandardDateFormat(
          mainDriver.dob
        )}`}</Text>
      </SummaryCard>
      <SummaryCard
        heading="License held for"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.MainDriver,
            MotorQuoteSubSection.YearsDriving
          )
        }
      >
        <Text>{`${mainDriver.yearsDriving} year${
          mainDriver.yearsDriving === 1 ? '' : 's'
        }`}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Most recent insurer"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.MainDriver,
            MotorQuoteSubSection.RecentInsurer
          )
        }
      >
        <Text>{getRecentInsurerName(funnelState, referenceData)}</Text>
        <Text>{getCoverLevelDescription(funnelState, referenceData)}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Claims made in the last 3 years"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.MainDriver,
            MotorQuoteSubSection.ClaimsMade
          )
        }
      >
        <Text>
          {mainDriver.claimsMade > 0
            ? `${mainDriver.claimsMade} claim${
                mainDriver.claimsMade === 1 ? '' : 's'
              } made`
            : 'None'}
        </Text>
      </SummaryCard>
      {mainDriver.claimsMade > 0 && (
        <SummaryCard
          heading="Claims where excess was paid"
          onEditClick={() =>
            onEditQuoteDetails(
              MotorSection.MainDriver,
              MotorQuoteSubSection.PayableClaims
            )
          }
        >
          <Text>
            {mainDriver.payableClaims > 0
              ? `${mainDriver.payableClaims} payable claim${
                  mainDriver.payableClaims === 1 ? '' : 's'
                }`
              : 'None'}
          </Text>
        </SummaryCard>
      )}
    </>
  );
};

export default MainDriverSummary;
